
import { useState, useEffect } from 'react';
import { trackAdView } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AdModalProps {
  isOpen: boolean;
  onClose: () => void;
  adType?: string;
}

const AdModal = ({ isOpen, onClose, adType = 'message_read' }: AdModalProps) => {
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(5);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCountdown(5);
      setCanClose(false);

      // Track ad view
      if (user) {
        trackAdView(user.id, adType);
      }

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanClose(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen, user, adType]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Close Button */}
        {canClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-gray-900/80 text-white rounded-full hover:bg-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        )}

        {/* Countdown Badge */}
        {!canClose && (
          <div className="absolute top-4 right-4 z-10 bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-bold">
            {countdown} saniye
          </div>
        )}

        {/* Ad Content */}
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-4">
              <i className="ri-advertisement-line text-3xl text-primary-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Reklam
            </h3>
            <p className="text-sm text-gray-600">
              GlobalMeet tamamen ücretsizdir. Reklamlar sayesinde size hizmet vermeye devam ediyoruz.
            </p>
          </div>

          {/* Sample Ad Content */}
          <div className="bg-gradient-to-br from-primary-50 to-pink-50 rounded-2xl p-8 mb-6">
            <div className="aspect-video bg-white rounded-xl flex items-center justify-center mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/f6c5010678e67062ace51b5bfe7b021f.jpg"
                alt="Advertisement"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">
              Örnek Reklam İçeriği
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Bu alan reklam içeriği için ayrılmıştır. Platformumuz tamamen ücretsizdir ve reklamlar sayesinde çalışmaya devam etmektedir.
            </p>
            <button className="w-full px-6 py-3 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer">
              Daha Fazla Bilgi
            </button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <i className="ri-information-line text-blue-600 text-xl"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Neden reklam gösteriyoruz?</p>
                <p className="text-xs">
                  GlobalMeet tamamen ücretsiz bir platformdur. Tüm özelliklerimizi ücretsiz sunabilmek için reklamlardan gelir elde ediyoruz. Desteğiniz için teşekkür ederiz!
                </p>
              </div>
            </div>
          </div>

          {/* Close Button (when countdown is done) */}
          {canClose && (
            <button
              onClick={onClose}
              className="w-full mt-6 px-6 py-3 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Devam Et
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdModal;
